import * as React from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {
  StyledTable,
  StyledTableCell,
  StyledIconButton,
  StyledDeleteIcon,
  StyledEmptyTableMessage
} from './UserManagementStyle'
import RemoveUserComponent from './RemoveUser/RemoveUserComponent'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessage from '../common/Snackbar/SnackbarMessages.json'
import DataRefetchOnError from '../common/DataFetchError/DataRefetchOnError'
import EditUserComponent from './EditUserDetails/EditUserComponent'
import PropTypes from 'prop-types'
import PermissionSectionComponent from '../common/PermissionSection/PermissionSectionComponent'
import {useParams} from 'react-router'
import {useGetGroupPermissionDetails} from '../api/getGroupProjectList'
import {formatDate} from './utils/dateFormatter'
import {adminUserListHeading, activeUserListHeading, archiveUserListHeading} from '../../appConfig/userList.config'
import CreateIcon from '@mui/icons-material/Create'

export const isExpiringSoon = (accessEnd) => {
  if (accessEnd === 'Indefinite' || !accessEnd) {
    return false
  }
  const parts = accessEnd.split('/')
  const month = Number.parseInt(parts[0], 10) - 1
  const day = Number.parseInt(parts[1], 10)
  const year = Number.parseInt(parts[2], 10) + 2000

  const endDate = new Date(year, month, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffTime = endDate - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays < 7
}

const columnConfig = {
  name: {
    label: 'Name',
    renderCell: (row) => `${row.FIRST_NAME} ${row.LAST_NAME}`
  },
  email: {
    label: 'User Email',
    renderCell: (row) => row.EMAIL_ID,
    style: {wordBreak: 'break-all', overflow: 'hidden', textOverflow: 'ellipsis'}
  },
  client: {
    label: 'Client',
    renderCell: (row) => row.CLIENT_NAME,
    style: {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}
  },
  project: {
    label: 'Project',
    renderCell: (row) => {
      return (
        <a
          style={{color: (theme) => theme.palette.hpPrimary}}
          href={`adminpanel/client/${row.CLIENT_ID}/project/${row.GROUP_ID}`}
          rel='noopener norefferer'
        >
          {row.PROJECT}
        </a>
      )
    },
    style: {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}
  },
  type: {
    label: 'Type',
    renderCell: (row) => (row.IS_TEMPORARY ? 'Temporary' : 'Permanent')
  },
  status: {
    label: 'Status',
    renderCell: (row) => (row.IS_ACTIVE ? 'Active' : 'Pending Acceptance')
  },
  archive: {
    label: 'Status',
    renderCell: (row) => (row.ARCHIVED ? row.ARCHIVED.charAt(0).toUpperCase() + row.ARCHIVED.slice(1) : row.ARCHIVED)
  },
  accessPeriod: {
    label: 'Access Period',
    renderCell: (row) => `${formatDate(row.START_DATE)} - ${formatDate(row.END_DATE)}`,
    style: (row) => ({
      color: isExpiringSoon(row.END_DATE)
        ? (theme) => theme.palette.hpAccentOrange
        : (theme) => theme.palette.hpAccentBlack
    })
  },
  addedBy: {
    label: 'Added to Project By',
    renderCell: (row) =>
      row.ADDED_BY_FIRST_NAME ? (
        `${row.ADDED_BY_FIRST_NAME} ${row.ADDED_BY_LAST_NAME}`
      ) : (
        <div style={{textAlign: 'center', width: '90%'}}>&mdash;</div>
      )
  },
  removedBy: {
    label: 'Removed By',
    renderCell: (row) =>
      row.REMOVED_BY_FIRST_NAME ? (
        `${row.REMOVED_BY_FIRST_NAME} ${row.REMOVED_BY_LAST_NAME}`
      ) : (
        <div style={{textAlign: 'center', width: '90%'}}>&mdash;</div>
      )
  },
  permissions: {
    label: 'Permissions',
    renderCell: (row) => (
      <PermissionSectionComponent selectedJurisdictionID={row.JURISDICTION} selectedFilterKeys={row.FILTERS} />
    )
  },
  actions: {
    label: 'Actions',
    renderCell: (row, handlers) => (
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <StyledIconButton
          aria-label={`edit-${row.FIRST_NAME} ${row.LAST_NAME}`}
          onClick={() => handlers.getUserNameForEditData(row)}
        >
          <CreateIcon variant='primaryIcon' data-testid='edit-icon' />
        </StyledIconButton>
        <StyledIconButton
          aria-label={`delete-${row.FIRST_NAME} ${row.LAST_NAME}`}
          onClick={() => handlers.getUserName(row)}
        >
          <StyledDeleteIcon data-testid='delete-icon' />
        </StyledIconButton>
      </div>
    )
  }
}

const UserDetailListItems = ({
  data,
  loading,
  handleDeleteUser,
  showNotification,
  setShowNotification,
  handleEditUser,
  error,
  errorDeletingUser,
  errorEditingUser,
  showNotificationForEdit,
  setShowNotificationForEdit,
  refetch,
  userAdded,
  deletedUser,
  isClientComponent,
  view
}) => {
  const [open, setOpen] = React.useState(false)
  const [removeUserData, setRemoveUserData] = React.useState('')
  const [editOpen, setEditOpen] = React.useState(false)
  const [editUserData, setEditUserData] = React.useState('')
  const {clientId, projectId} = useParams()
  const {groupPermissionListDetails} = useGetGroupPermissionDetails(projectId, clientId)
  const selectedJurisdictionIndex = groupPermissionListDetails?.groupPermissionList?.[0]?.JURISDICTION
  const selectedFilterKeys = groupPermissionListDetails?.groupPermissionList?.[0]?.FILTERS
  let dataColumns
  if (isClientComponent) {
    dataColumns = adminUserListHeading
  } else if (view === 'archive') {
    dataColumns = archiveUserListHeading
  } else {
    dataColumns = activeUserListHeading
  }
  const getUserName = (rowData) => {
    setRemoveUserData(rowData)
    setOpen(true)
  }

  const getUserNameForEditData = (rowData) => {
    setEditUserData(rowData)
    setEditOpen(true)
  }

  const refetchChart = (event) => {
    event.preventDefault()
    refetch()
  }

  if (!loading && error) {
    return <DataRefetchOnError data-testid='data-refetch-on-error' onTryAgain={(event) => refetchChart(event)} />
  }

  const getMessage = () => {
    return userAdded ? SnackbarMessage.addUserSuccess : deleteUserMessage()
  }

  const deleteUserMessage = () => {
    return deletedUser ? SnackbarMessage.userDeleteSuccess : SnackbarMessage.userDeleteError
  }

  const tableAriaLabel = `Table of project users, ${data?.length} users total`

  const columnHeader = () => {
    return (
      <TableRow>
        {dataColumns.map((columnKey) => (
          <StyledTableCell
            align={columnKey === 'actions' ? 'right' : ''}
            key={columnKey}
            tabIndex={0}
            aria-label={columnConfig[columnKey]?.label || ''}
          >
            {columnConfig[columnKey]?.label || ''}
          </StyledTableCell>
        ))}
      </TableRow>
    )
  }
  const rowContent = () => {
    return data.map((row, index) => {
      if (view === 'all') {
        row.JURISDICTION = selectedJurisdictionIndex
        row.FILTERS = selectedFilterKeys
      }
      return (
        <TableRow key={`${index}_${row.EMAIL_ID}`}>
          {dataColumns.map((columnKey) => (
            <StyledTableCell
              key={columnKey}
              tabIndex={0}
              style={
                typeof columnConfig[columnKey]?.style === 'function'
                  ? columnConfig[columnKey].style(row)
                  : columnConfig[columnKey]?.style || {}
              }
            >
              {columnConfig[columnKey]?.renderCell(row, {getUserName, getUserNameForEditData}) || ''}
            </StyledTableCell>
          ))}
        </TableRow>
      )
    })
  }

  return (
    <>
      {!loading && data && (
        <StyledTable elevation={0} tabIndex={0} aria-label={tableAriaLabel}>
          <Table aria-label='simple table'>
            <TableHead>{columnHeader()}</TableHead>
            <TableBody>
              {data.length > 0 ? (
                rowContent()
              ) : (
                <TableRow>
                  <StyledEmptyTableMessage colSpan={dataColumns.length} tabIndex={0}>
                    No user added yet to this Project
                  </StyledEmptyTableMessage>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTable>
      )}
      {open && (
        <RemoveUserComponent open={open} data={removeUserData} setOpen={setOpen} handleDeleteUser={handleDeleteUser} />
      )}
      {editOpen && (
        <EditUserComponent
          open={editOpen}
          data={editUserData}
          setOpen={setEditOpen}
          handleEditUser={handleEditUser}
          isClient={isClientComponent}
        />
      )}

      {showNotification && (
        <SnackbarComponent
          error={errorDeletingUser}
          showNotification={showNotification}
          setShowNotification={setShowNotification}
          message={getMessage()}
          isClient={isClientComponent}
        />
      )}
      {showNotificationForEdit && (
        <SnackbarComponent
          error={errorEditingUser}
          showNotification={showNotificationForEdit}
          setShowNotification={setShowNotificationForEdit}
          message={errorEditingUser ? SnackbarMessage.userEditError : SnackbarMessage.userEditSuccess}
          isClient={isClientComponent}
        />
      )}
    </>
  )
}

UserDetailListItems.propTypes = {
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  handleDeleteUser: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  setShowNotification: PropTypes.func.isRequired,
  handleEditUser: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  errorDeletingUser: PropTypes.bool.isRequired,
  errorEditingUser: PropTypes.bool.isRequired,
  showNotificationForEdit: PropTypes.bool.isRequired,
  setShowNotificationForEdit: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  userAdded: PropTypes.bool.isRequired,
  deletedUser: PropTypes.bool.isRequired,
  isClientComponent: PropTypes.bool.isRequired,
  view: PropTypes.object.isRequired
}

export default UserDetailListItems

/*
 * 1. The component renders a table with user details.
 * 2. It handles the delete and edit actions for users.
 * 3. It shows notifications based on the actions performed.
 * 4. It uses Material UI components for styling and layout.
 */
